const SVG_NS = 'http://www.w3.org/2000/svg';

let svgOverlay: SVGSVGElement | null = null;

// Tracking loop state — see `drawArrows` for why this exists. The
// assignment map / board reference live here (not in the loop's
// closure) so a fresh drawArrows call can update both for the running
// loop without spawning a parallel one.
let trackingHandle: number | null = null;
let trackingDeadline = 0;
let trackingAssignments: Map<string, string> = new Map();
let trackingBoard: HTMLElement | null = null;
// How long after a `drawArrows` call we keep re-reading bounding rects
// each frame. Long enough to cover any synchronous post-render cleanup
// (e.g. DragController.endSession clearing the drag's inline transform
// so the dropped card snaps back to its ring slot), plus any short CSS
// transitions on attacker / blocker poses. Past this window, the
// arrows stay put until the next state change re-calls drawArrows.
const TRACKING_DURATION_MS = 300;

export function createArrowOverlay(parent: HTMLElement): SVGSVGElement {
  if (svgOverlay) {
    svgOverlay.remove();
  }

  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.classList.add('combat-arrows');
  svg.setAttribute('width', '100%');
  svg.setAttribute('height', '100%');

  // Arrow marker definition
  const defs = document.createElementNS(SVG_NS, 'defs');
  const marker = document.createElementNS(SVG_NS, 'marker');
  marker.setAttribute('id', 'arrowhead');
  marker.setAttribute('markerWidth', '8');
  marker.setAttribute('markerHeight', '6');
  marker.setAttribute('refX', '8');
  marker.setAttribute('refY', '3');
  marker.setAttribute('orient', 'auto');
  const polygon = document.createElementNS(SVG_NS, 'polygon');
  polygon.setAttribute('points', '0 0, 8 3, 0 6');
  polygon.setAttribute('fill', '#e94560');
  marker.appendChild(polygon);
  defs.appendChild(marker);
  svg.appendChild(defs);

  parent.appendChild(svg);
  svgOverlay = svg;
  return svg;
}

function drawOnce(
  blockerAssignments: Map<string, string>,
  boardEl: HTMLElement,
): void {
  if (!svgOverlay) return;

  // Clear existing arrows
  const existing = svgOverlay.querySelectorAll('.combat-arrow');
  existing.forEach(el => el.remove());

  const boardRect = boardEl.getBoundingClientRect();

  for (const [attackerId, blockerId] of blockerAssignments) {
    const attackerEl = boardEl.querySelector(`.card[data-instance-id="${attackerId}"]`) as HTMLElement | null;
    const blockerEl = boardEl.querySelector(`.card[data-instance-id="${blockerId}"]`) as HTMLElement | null;
    if (!attackerEl || !blockerEl) continue;

    const aRect = attackerEl.getBoundingClientRect();
    const bRect = blockerEl.getBoundingClientRect();

    // Compute centers relative to the board
    const ax = aRect.left + aRect.width / 2 - boardRect.left;
    const ay = aRect.top + aRect.height / 2 - boardRect.top;
    const bx = bRect.left + bRect.width / 2 - boardRect.left;
    const by = bRect.top + bRect.height / 2 - boardRect.top;

    const line = document.createElementNS(SVG_NS, 'line');
    line.classList.add('combat-arrow');
    line.setAttribute('x1', String(ax));
    line.setAttribute('y1', String(ay));
    line.setAttribute('x2', String(bx));
    line.setAttribute('y2', String(by));
    line.setAttribute('marker-end', 'url(#arrowhead)');
    svgOverlay.appendChild(line);
  }
}

/** Draw the attacker→blocker arrows AND keep tracking them via rAF for
 *  a short window (TRACKING_DURATION_MS). The tracking loop matters
 *  because the cards we anchor to can move *after* the synchronous
 *  render that called us:
 *
 *  - On a successful blocker-assignment drop, DragController dispatches
 *    the action synchronously (firing this drawArrows), then clears the
 *    drag's inline `transform` in its endSession cleanup — snapping the
 *    blocker from the drop point back to its ring slot. Without
 *    tracking, the arrow stays pinned to the drop point.
 *  - CSS transitions on attacker/blocker poses (the translateY lean)
 *    also move the cards over ~200ms after the class toggle. Tracking
 *    lets the arrow follow that motion frame-by-frame instead of
 *    sitting at the pre-animation position until the next state push.
 *
 *  The handle / deadline pair lets a fresh call extend the window
 *  rather than running multiple loops in parallel. */
export function drawArrows(
  blockerAssignments: Map<string, string>,
  boardEl: HTMLElement,
): void {
  if (!svgOverlay) return;

  drawOnce(blockerAssignments, boardEl);

  // (Re)start the tracking loop. A fresh call updates the shared
  // assignment / board / deadline state in place; any in-flight loop
  // picks up the new values on its next frame.
  trackingAssignments = blockerAssignments;
  trackingBoard = boardEl;
  trackingDeadline = performance.now() + TRACKING_DURATION_MS;
  if (trackingHandle !== null) return;
  const step = (now: number): void => {
    if (now >= trackingDeadline || !svgOverlay || !trackingBoard) {
      trackingHandle = null;
      return;
    }
    drawOnce(trackingAssignments, trackingBoard);
    trackingHandle = requestAnimationFrame(step);
  };
  trackingHandle = requestAnimationFrame(step);
}

export function clearArrows(): void {
  if (!svgOverlay) return;
  const existing = svgOverlay.querySelectorAll('.combat-arrow');
  existing.forEach(el => el.remove());
  // Cancel any in-flight tracking — otherwise the loop would redraw
  // arrows from a stale assignment map after the caller cleared them.
  if (trackingHandle !== null) {
    cancelAnimationFrame(trackingHandle);
    trackingHandle = null;
  }
}
